import React, { useEffect, useRef, useMemo } from "react";
import Select from "components/ui/Select";
import { useAllFinancialyears } from "http-hooks/financialyear.js/useFinancialyear";
import { formatDateAndTime } from "utils/date";

const SelectFinancialYears = ({
  value,
  onChange,
  placeholder = "Select Financial Year...",
  isDisabled = false,
  isRequired = false,
  label = "Financial Year",
  className = "",
  filters = {},
  showLabel = true,
  initialValue = null, // Expected: { srno: "value" }
  maxMenuHeight = 100, // Limit dropdown height to show scrollbar
}) => {
  const hasInitialized = useRef(false);
  
  const {
    isLoading: isLoadingAllFinancialYears,
    data: { data: allFinancialYears } = [],
  } = useAllFinancialyears(filters);

  // Convert financial years to options for Select component
  const financialYearOptions = useMemo(() => {
    return allFinancialYears?.map(fy => ({
      value: fy.srno,
      label: `${formatDateAndTime(fy.fys, "YYYY")} - ${formatDateAndTime(fy.fye, "YYYY")}`,
      srno: fy.srno,
      fys: fy.fys,
      fye: fy.fye,
      ...fy
    })) || [];
  }, [allFinancialYears]);

  // Handle initial value selection
  useEffect(() => {
    if (
      !hasInitialized.current && 
      initialValue &&
      financialYearOptions.length > 0 && 
      !isLoadingAllFinancialYears &&
      onChange
    ) {
      // Check if required field is present
      if (!initialValue.srno) {
        const error = new Error(
          `SelectFinancialYears: initialValue must contain srno field. Received: ${JSON.stringify(initialValue)}`
        );
        console.error(error.message);
        throw error;
      }

      const matchedOption = financialYearOptions.find(option => option.srno === initialValue.srno);
      
      if (matchedOption) {
        onChange(matchedOption);
      }
      
      hasInitialized.current = true;
    }
  }, [financialYearOptions, initialValue, isLoadingAllFinancialYears, onChange]);

  return (
    <Select
      value={value}
      onChange={onChange}
      options={financialYearOptions}
      getOptionValue={(option) => option?.value}
      getOptionLabel={(option) => option?.label}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isLoading={isLoadingAllFinancialYears}
      isRequired={isRequired}
      label={label}
      className={className}
      showLabel={showLabel}
      isClearable={true}
      isSearchable={true}
      maxMenuHeight={maxMenuHeight}
    />
  );
};

export default SelectFinancialYears; 